// leaveTypes&Policies.jsx
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchEmployees } from "../redux/actions/employeeActions";
import theme from "../components/shared/theme";
import {
  dropdownStyle,
  thStyle,
  tdStyle,
  paginationButtonStyle,
} from "../components/shared/themeStyles";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { fetchDistricts, fetchMunicipalities } from "../redux/actions/locationActions";
import AssignLeaveModal from "../pages/Leave Management/AssignLeaveModal"; // <-- import modal

const LeaveTypesPolicies = () => {
  const dispatch = useDispatch();
  const { employees, loading } = useSelector((state) => state.employee);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedMunicipality, setSelectedMunicipality] = useState("");
  const [selectedWard, setSelectedWard] = useState("");
  const [selectedDesignation, setSelectedDesignation] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleEdit = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  useEffect(() => {
    setSelectedMunicipality(""); // Reset municipality when district changes
  }, [selectedDistrict]);

  const itemsPerPage = 10;

  useEffect(() => {
    dispatch(fetchEmployees());
    dispatch(fetchDistricts());
    dispatch(fetchMunicipalities());
  }, [dispatch]);

  const { districts, municipalities } = useSelector((state) => state.location);

  const wards = [...new Set(employees.map((e) => e.ward).filter(Boolean))];
  const statuses = [...new Set(employees.map((e) => e.status).filter(Boolean))];

  const sortedEmployees = [...employees].sort((a, b) => {
    if (!sortField) return 0;
    const aValue = a[sortField] || "";
    const bValue = b[sortField] || "";
    if (sortOrder === "asc") return aValue > bValue ? 1 : -1;
    else return aValue < bValue ? 1 : -1;
  });

  const filteredEmployees = sortedEmployees.filter((emp) => {
    const term = searchTerm.toLowerCase();
    return (
      (!selectedDistrict || emp.district === selectedDistrict) &&
      (!selectedMunicipality || emp.municipality === selectedMunicipality) &&
      (!selectedWard || emp.ward === selectedWard) &&
      (!selectedDesignation || emp.designation === selectedDesignation) &&
      (!selectedStatus || emp.status === selectedStatus) &&
      ((emp?.name || "").toLowerCase().includes(term) ||
        (emp?.email || "").toLowerCase().includes(term) ||
        (emp?.phone || "").toLowerCase().includes(term) ||
        (emp?.designation || "").toLowerCase().includes(term))
    );
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const currentItems = filteredEmployees.slice(indexOfLastItem - itemsPerPage, indexOfLastItem);
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // Find the selected district's ID
  const selectedDistrictObj = districts.find((d) => d.name === selectedDistrict);
  const selectedDistrictId = selectedDistrictObj?.id;

  // Filter municipalities by selectedDistrictId
  const filteredMunicipalities = selectedDistrictId
    ? municipalities.filter((m) => m.district_id === selectedDistrictId)
    : municipalities;

  return (
    <div style={{ padding: theme.spacing.lg, backgroundColor: theme.colors.background, minHeight: "100vh" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: theme.spacing.lg }}>
        <h1 style={{ fontSize: theme.font.size["3xl"], fontWeight: theme.font.weight.bold, color: theme.colors.grayDark }}>
          Leave Types & Policies
        </h1>
      </div>

      {/* Filters */}
      <div style={{ marginBottom: theme.spacing.lg }}>
        <select value={selectedDistrict} onChange={(e) => setSelectedDistrict(e.target.value)} style={dropdownStyle}>
          <option value="">All Districts</option>
          {districts.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
        </select>

        <select
          value={selectedMunicipality}
          onChange={(e) => setSelectedMunicipality(e.target.value)}
          style={dropdownStyle}
        >
          <option value="">All Municipalities</option>
          {filteredMunicipalities.map((m) => (
            <option key={m.id} value={m.name}>{m.name}</option>
          ))}
        </select>

        <select value={selectedDesignation} onChange={(e) => setSelectedDesignation(e.target.value)} style={dropdownStyle}>
          <option value="">All Designations</option>
          {[...new Set(employees.map((e) => e.designation).filter(Boolean))].map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>

        <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} style={dropdownStyle}>
          <option value="">All Status</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        <input
          type="text"
          placeholder="Search by name, email, phone or designation"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: theme.spacing.sm,
            marginLeft: theme.spacing.sm,
            border: "1px solid #ccc",
            borderRadius: theme.radius.md,
            minWidth: "250px"
          }}
        />
      </div>

      {/* Table */}
      <div style={{ backgroundColor: "#fff", borderRadius: theme.radius.lg, boxShadow: theme.shadow.soft, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: theme.font.size.sm }}>
          <thead style={{ backgroundColor: theme.colors.grayDark, color: "#fff" }}>
            <tr>
              {["id", "photo", "name", "phone", "designation", "district", "municipality","status", "actions"].map((field) => (
                <th
                  key={field}
                  style={thStyle}
                  onClick={() => toggleSort(field)}
                >
                  {field.toUpperCase()}{sortField === field ? (sortOrder === "asc" ? " ↑" : " ↓") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentItems.map((emp) => (
              <tr key={emp.id} style={{ borderBottom: "1px solid #eee" }}>
                <td style={tdStyle}>{emp.id}</td>
                <td style={tdStyle}>
                  <img
                    src={emp.photo || "https://img.freepik.com/free-vector/blue-circle-with-white-user_78370-4707.jpg"}
                    alt="Profile"
                    style={{ height: "40px", width: "40px", borderRadius: theme.radius.full }}
                  />
                </td>
                <td style={tdStyle}>{emp.name}</td>
                <td style={tdStyle}>{emp.phone}</td>
                <td style={tdStyle}>{emp.designation}</td>
                <td style={tdStyle}>{emp.district}</td>
                <td style={tdStyle}>{emp.municipality}</td>
                <td style={tdStyle}>{emp.status}</td>
                <td style={tdStyle}>
                  <button
                    onClick={() => handleEdit(emp)}
                    style={{ backgroundColor: theme.colors.primary, color: "#fff", padding: "4px 8px", borderRadius: theme.radius.sm }}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: theme.spacing.md }}>
        <button
          onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
          style={paginationButtonStyle}
        >
          <FiChevronLeft /> Previous
        </button>
        <span style={{ fontSize: theme.font.size.sm }}>Page {currentPage} of {totalPages}</span>
        <button
          onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={paginationButtonStyle}
        >
          Next <FiChevronRight />
        </button>
      </div>

      {/* Assign Leave Modal */}
      {showModal && (
        <AssignLeaveModal
          user={selectedUser}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default LeaveTypesPolicies;
